import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, CheckCircle2, Mail, MapPin, Phone, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { ROLES, PROJECT_TYPES, BUDGETS, SITE } from '../lib/site';
import { OFFICE_NIGHT } from '../lib/imagery';
import { useBrandImage } from '../lib/BrandImagesContext';
import { useJsonLd, breadcrumbSchema, siteOrigin } from '../lib/seo';

export default function ContactPage() {
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const officeImage = useBrandImage('contact.office', OFFICE_NIGHT);
    const origin = siteOrigin();

    // Structured data — ContactPage + Breadcrumb
    useJsonLd('contact-page', {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': `${origin}/contact#page`,
        url: `${origin}/contact`,
        name: 'Contact · DigiTech Cloud AI',
        inLanguage: 'en',
        about: { '@id': `${origin}/#organization` },
        mainEntity: {
            '@type': 'Organization',
            '@id': `${origin}/#organization`,
            name: 'DigiTech Cloud AI',
            email: 'info@digitechcloud-ai.com',
            telephone: '+1-307-200-0734',
            contactPoint: [{
                '@type': 'ContactPoint',
                telephone: '+1-307-200-0734',
                email: 'info@digitechcloud-ai.com',
                contactType: 'customer service',
                areaServed: ['US', 'GB', 'NG', 'KE', 'ZA', 'GH', 'RW', 'SN', 'CI', 'EG', 'MA', 'AE', 'SA', 'QA', 'DE', 'FR', 'SG', 'IN'],
                availableLanguage: ['en', 'fr', 'ar'],
            }],
        },
    });
    useJsonLd('contact-breadcrumb', breadcrumbSchema([
        { name: 'Home',    url: `${origin}/` },
        { name: 'Contact', url: `${origin}/contact` },
    ]));
    const [form, setForm] = useState({
        name: '',
        email: '',
        company: '',
        role: '',
        project_type: '',
        budget: '',
        message: '',
        consent: true,
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error('Please confirm consent to continue.');
            return;
        }
        if (form.name.trim().length < 2 || !form.email || form.message.trim().length < 4) {
            toast.error('Please fill name, email, and a brief message.');
            return;
        }
        try {
            setSubmitting(true);
            await api.post('/leads', form);
            setSuccess(true);
            toast.success('Enquiry received — we will be in touch shortly.');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not submit. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="relative pt-32 lg:pt-36 pb-12">
                <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="dt-section-eyebrow">CONTACT</span>
                        <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
                            Let’s discuss your highest-value <span className="dt-gradient-text">digital challenge</span>.
                        </h1>
                        <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">
                            Tell us what you want to improve, build, modernise, or scale. Our team will review your enquiry and respond with the right senior technology specialist to discuss objectives, architecture, scope, delivery options, and next steps.
                        </p>

                        {/* Human context image */}
                        <div className="mt-8 relative rounded-2xl overflow-hidden border border-white/10 dt-glow-cyan">
                            <div className="aspect-[16/10] w-full">
                                <img
                                    src={officeImage}
                                    alt=""
                                    aria-hidden
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    style={{ filter: 'saturate(0.6) hue-rotate(-6deg) brightness(0.7)' }}
                                    onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = 'none'; }}
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.98)] via-[rgba(8,12,20,0.55)] to-[rgba(8,12,20,0.1)]" />
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                                <div className="font-mono text-[10px] tracking-[0.22em] text-primary">
                                    RESPONSE TIME · TYPICALLY WITHIN ONE BUSINESS DAY
                                </div>
                                <div className="mt-1 text-sm font-medium text-foreground/95">
                                    A senior technology specialist reviews every enquiry.
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 grid grid-cols-1 gap-3">
                            <TrustItem icon={<ShieldCheck className="h-4 w-4" />} title="Confidential"   subtitle="A mutual NDA can be arranged where appropriate" />
                            <TrustItem icon={<CheckCircle2 className="h-4 w-4" />} title="Senior-led"    subtitle="Business and engineering perspectives from the outset" />
                            <TrustItem icon={<Clock className="h-4 w-4" />}       title="Clear"         subtitle="Scope, delivery approach, responsibilities, and timeline" />
                        </div>

                        <div className="mt-10 space-y-3 text-sm text-foreground/80">
                            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> <a href={`mailto:${SITE.email}`} className="hover:text-primary">Email: {SITE.email}</a></div>
                            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> <a href={`tel:${SITE.phone.replace(/[^+\d]/g, '')}`} className="hover:text-primary">Telephone: {SITE.phone}</a></div>
                            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary mt-0.5" /> <span>Address: {SITE.address}</span></div>
                            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> <span>Availability: {SITE.hours}</span></div>
                        </div>
                        <div className="mt-4 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                            United Kingdom subsidiary · Metatron Technologies Ltd
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                            {success ? (
                                <SuccessPanel onAgain={() => { setSuccess(false); setForm({ name: '', email: '', company: '', role: '', project_type: '', budget: '', message: '', consent: true }); }} />
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    data-testid="consultation-form"
                                    className="dt-glass rounded-2xl p-6 sm:p-8 space-y-5"
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field label="Full name" required>
                                            <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Jane Doe" data-testid="consultation-name-input" className="bg-white/5 border-white/10 focus-visible:ring-primary" required />
                                        </Field>
                                        <Field label="Work email" required>
                                            <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="jane@company.com" data-testid="consultation-email-input" className="bg-white/5 border-white/10 focus-visible:ring-primary" required />
                                        </Field>
                                        <Field label="Company">
                                            <Input value={form.company} onChange={(e) => update('company', e.target.value)} placeholder="Acme Corp" data-testid="consultation-company-input" className="bg-white/5 border-white/10 focus-visible:ring-primary" />
                                        </Field>
                                        <Field label="Your role">
                                            <Select value={form.role} onValueChange={(v) => update('role', v)}>
                                                <SelectTrigger data-testid="consultation-role-select" className="bg-white/5 border-white/10 focus:ring-primary"><SelectValue placeholder="Select role" /></SelectTrigger>
                                                <SelectContent>
                                                    {ROLES.map((r) => (
                                                        <SelectItem key={r} value={r}>{r}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Project type">
                                            <Select value={form.project_type} onValueChange={(v) => update('project_type', v)}>
                                                <SelectTrigger data-testid="consultation-project-type-select" className="bg-white/5 border-white/10 focus:ring-primary"><SelectValue placeholder="Select type" /></SelectTrigger>
                                                <SelectContent>
                                                    {PROJECT_TYPES.map((r) => (
                                                        <SelectItem key={r} value={r}>{r}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Estimated investment range">
                                            <Select value={form.budget} onValueChange={(v) => update('budget', v)}>
                                                <SelectTrigger data-testid="consultation-budget-select" className="bg-white/5 border-white/10 focus:ring-primary"><SelectValue placeholder="Select range" /></SelectTrigger>
                                                <SelectContent>
                                                    {BUDGETS.map((r) => (
                                                        <SelectItem key={r} value={r}>{r}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    </div>
                                    <Field label="What challenge are you seeking to solve, or what would you like to build or transform?" required>
                                        <Textarea
                                            value={form.message}
                                            onChange={(e) => update('message', e.target.value)}
                                            placeholder="Briefly describe your goals, current stack, and timeline."
                                            rows={5}
                                            data-testid="consultation-message-input"
                                            className="bg-white/5 border-white/10 focus-visible:ring-primary"
                                            required
                                        />
                                    </Field>
                                    <label className="flex items-start gap-2 text-sm text-foreground/80">
                                        <Checkbox checked={form.consent} onCheckedChange={(v) => update('consent', !!v)} data-testid="consultation-consent-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                                        <span>I agree that DigiTech Cloud AI may use the information provided to respond to my enquiry.</span>
                                    </label>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="consultation-form-submit-button"
                                        size="lg"
                                        className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_24px_rgba(0,229,255,0.18)]"
                                    >
                                        {submitting ? 'Sending…' : 'Submit Enquiry'}
                                        <ArrowUpRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </form>
                            )}
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">
                {label}{required ? <span className="text-primary ml-1">*</span> : null}
            </Label>
            {children}
        </div>
    );
}

function TrustItem({ icon, title, subtitle }) {
    return (
        <div className="flex items-start gap-3 dt-glass rounded-xl p-3.5">
            <span className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary shrink-0">{icon}</span>
            <div>
                <div className="text-sm font-medium">{title}</div>
                <div className="text-xs text-muted-foreground">{subtitle}</div>
            </div>
        </div>
    );
}

function SuccessPanel({ onAgain }) {
    return (
        <div data-testid="consultation-form-success-message" className="dt-neon-border">
            <div className="dt-neon-inner rounded-[var(--radius)] p-8 sm:p-12 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 border border-primary/40 inline-flex items-center justify-center text-primary">
                    <CheckCircle2 className="h-7 w-7" />
                </div>
                <h2 className="font-display mt-4 text-2xl sm:text-3xl font-semibold">Enquiry received</h2>
                <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                    Thank you. Our team will review your enquiry and reach out with the right senior technology specialist to discuss next steps.
                </p>
                <div className="mt-6 flex justify-center gap-3">
                    <Button onClick={onAgain} variant="secondary" className="border border-white/10">Send another</Button>
                </div>
            </div>
        </div>
    );
}
